const CLOSING_SOON_THRESHOLD = 90; // minutes

// Matches a time range like "10:00-17:00", "10–17", "10.00-17.00",
// "09:00 – 18:00 Uhr". Supports ASCII hyphen, en-dash, em-dash and optional
// minutes (so "10-17" is read as 10:00–17:00).
const TIME_RANGE_RE = /(\d{1,2})(?:[:.](\d{2}))?\s*[-–—‐‑]\s*(\d{1,2})(?:[:.](\d{2}))?/;

function isClosedString(value) {
  if (!value) return true;
  const v = value.trim().toLowerCase();
  return !v || v === 'geschlossen' || v === 'closed' || v === 'ruhetag';
}

function parseTimeRange(value) {
  if (!value) return null;
  const match = value.match(TIME_RANGE_RE);
  if (!match) return null;
  const openHour = parseInt(match[1], 10);
  const openMin = match[2] ? parseInt(match[2], 10) : 0;
  const closeHour = parseInt(match[3], 10);
  const closeMin = match[4] ? parseInt(match[4], 10) : 0;
  const openTime = openHour * 60 + openMin;
  let closeTime = closeHour * 60 + closeMin;
  // 24:00 → end of day (1440 minutes)
  if (closeHour === 24 && closeMin === 0) closeTime = 1440;
  return { openHour, openMin, closeHour, closeMin, openTime, closeTime };
}

function fmtTime(hour, min) {
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Opening hours of one weekday (0 = Sunday … 6 = Saturday), or null when the
 * place is closed that day, the hours are missing, or they can't be read.
 */
export function getDayOpeningHours(openingHours, dayIndex, alwaysOpen = false) {
  if (alwaysOpen) return { openTime: '06:00', closeTime: '22:00' };
  const hours = openingHours?.[DAY_NAMES[dayIndex]];
  if (isClosedString(hours)) return null;
  const parsed = parseTimeRange(hours);
  if (!parsed) return null;
  return { openTime: fmtTime(parsed.openHour, parsed.openMin), closeTime: fmtTime(parsed.closeHour, parsed.closeMin) };
}

export function useOpeningHours() {
  const checkOpenNow = (openingHours, alwaysOpen = false) => {
    if (alwaysOpen) {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      const openTime = 6 * 60;   // 06:00
      const closeTime = 22 * 60; // 22:00
      const isOpen = currentTime >= openTime && currentTime < closeTime;
      const closingSoon = isOpen && (closeTime - currentTime) < CLOSING_SOON_THRESHOLD;
      return {
        isOpen,
        status: isOpen ? (closingSoon ? 'closing_soon' : 'open') : 'closed',
        openTime: '06:00',
        closeTime: '22:00'
      };
    }
    if (!openingHours) return { isOpen: null, status: 'unknown' };

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

    const todayHours = openingHours[currentDay];

    if (isClosedString(todayHours)) {
      return { isOpen: false, status: 'closed_today' };
    }

    const parsed = parseTimeRange(todayHours);
    if (!parsed) {
      return { isOpen: null, status: 'unknown' };
    }

    const { openTime, closeTime, openHour, openMin, closeHour, closeMin } = parsed;

    const isOpen = currentTime >= openTime && currentTime < closeTime;
    const closingSoon = isOpen && (closeTime - currentTime) < CLOSING_SOON_THRESHOLD;

    return {
      isOpen,
      status: isOpen ? (closingSoon ? 'closing_soon' : 'open') : 'closed',
      openTime: fmtTime(openHour, openMin),
      closeTime: fmtTime(closeHour, closeMin)
    };
  };

  const getNextOpenInfo = (openingHours) => {
    if (!openingHours) return null;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayLabels = {
      de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    };
    const now = new Date();
    const todayIdx = now.getDay();

    for (let i = 1; i <= 7; i++) {
      const idx = (todayIdx + i) % 7;
      const dayName = dayNames[idx];
      const hours = openingHours[dayName];
      if (isClosedString(hours)) continue;
      const parsed = parseTimeRange(hours);
      if (!parsed) continue;
      const label = i === 1 ? { de: 'morgen', en: 'tomorrow' } : { de: dayLabels.de[idx], en: dayLabels.en[idx] };
      return {
        dayLabel: label,
        openTime: fmtTime(parsed.openHour, parsed.openMin),
        closeTime: fmtTime(parsed.closeHour, parsed.closeMin)
      };
    }
    return null;
  };

  const getStatusLabel = (status, language = 'de') => {
    const labels = {
      de: {
        open: 'Geöffnet',
        closing_soon: 'Schliesst bald',
        closed: 'Geschlossen',
        closed_today: 'Heute geschlossen',
        unknown: ''
      },
      en: {
        open: 'Open',
        closing_soon: 'Closing soon',
        closed: 'Closed',
        closed_today: 'Closed today',
        unknown: ''
      }
    };

    return labels[language]?.[status] || '';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-700 border-green-200',
      closing_soon: 'bg-orange-100 text-orange-700 border-orange-200',
      closed: 'bg-red-100 text-red-700 border-red-200',
      closed_today: 'bg-gray-100 text-gray-700 border-gray-200',
      unknown: 'bg-gray-50 text-gray-500 border-gray-100'
    };

    return colors[status] || colors.unknown;
  };

  return {
    checkOpenNow,
    getNextOpenInfo,
    getStatusLabel,
    getStatusColor
  };
}

export default useOpeningHours;